import { useState, useMemo, useCallback, useEffect, useRef } from "react"
import { Box, Flex, Center, Skeleton } from "@chakra-ui/react"
import { Sidebar } from "./Sidebar"
import { useSection } from "../../hooks/useSection"
import { Suspense } from "react"
import { SectionLoader } from "../sections/SectionLoader"
import { sections } from "../../sections"
import { SectionKey } from "../../sections"
import { Spinner } from "@chakra-ui/react"
import { useQuery } from "@apollo/client"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { SectionFeatures } from "@hydrologiq/shared/types/sectionFeatures"
import { useActiveOrganization, useAuth } from "../../auth/AuthProvider"
import { useChatbot, useKeyboardShortcut, ChatDrawer, ChatButton } from "../AskHydrologiq"
import { useFeatureEnabled, useFeatureToggles } from "../../hooks/useFeatureToggles"
import { SectionName } from "../../state/sectionSlice"
import { useLocation, useSearchParams } from "react-router-dom"
import { isGlobalSettingsRoute, parseRouteFromPath } from "../../utils/routeSync"
import { TopBar } from "./TopBar"
import { TenantProfile } from "@hydrologiq/gql-experience/graphql"
import { useDispatch } from "react-redux"
import { setPromotionOverrides } from "../../state/preferencesSlice"
import { usePreviewMode } from "../../hooks/usePreviewMode"
import { PreviewBanner } from "../PreviewBanner"

interface MainLayoutProps {
  outletContent?: React.ReactNode
}

export const MainLayout = ({ outletContent }: MainLayoutProps) => {
  const [sectionLoadingStates, setSectionLoadingStates] = useState<Record<string, boolean>>({})

  const { activeSection: currentSection, lastActiveSection, mountedSections } = useSection()
  const { activeOrganization } = useActiveOrganization()
  const { user } = useAuth()
  const location = useLocation()
  const { previewHash, isPreviewActive, previewBundleUrl } = usePreviewMode()
  const [searchParams, setSearchParams] = useSearchParams()

  // Check if we're on a global settings route
  const isOnGlobalSettings = isGlobalSettingsRoute(location.pathname)

  // Memoize expensive computations that were happening in render
  const { sectionsToRender, parsedUrlRouteInfo } = useMemo(() => {
    const parsedUrlRouteInfo = parseRouteFromPath(location.pathname)
    const effectiveActiveSection =
      currentSection ||
      (parsedUrlRouteInfo?.sectionName as SectionName | undefined) ||
      (isOnGlobalSettings ? lastActiveSection : null)

    // Pre-compute section rendering data to avoid computations in render
    const sectionsToRender = mountedSections.map((sectionName) => {
      const isActive = effectiveActiveSection === sectionName
      const shouldRender = sectionName in sections

      return {
        sectionName,
        isActive,
        shouldRender,
        urlRouteInfo: parsedUrlRouteInfo?.sectionName,
        currentSection,
      }
    })

    return { sectionsToRender, parsedUrlRouteInfo }
  }, [mountedSections, currentSection, location.pathname, isOnGlobalSettings, lastActiveSection, sections])

  // Determine if we're waiting for a section to load
  // This happens when we have a route but the section isn't mounted yet, or is still loading
  // Check URL directly first (synchronous), then fall back to Redux state
  const expectedSection: SectionName | null | undefined =
    currentSection || (parsedUrlRouteInfo?.sectionName as SectionName | undefined)
  const isSectionMounted = expectedSection ? mountedSections.includes(expectedSection) : false
  const isSectionLoading = expectedSection ? sectionLoadingStates[expectedSection] === true : false
  // Show loading if:
  // 1. Section is expected but not mounted yet (waiting for mount)
  // 2. OR section is mounted but still loading (waiting for load to complete)
  // Don't show loading if section is mounted and not loading, even if Redux hasn't updated yet
  const isLoadingSection = expectedSection && (!isSectionMounted || isSectionLoading) && !isOnGlobalSettings

  // Handler to track section loading state
  const handleSectionLoadStateChange = useCallback((sectionName: string, isLoading: boolean) => {
    setSectionLoadingStates((prev) => ({
      ...prev,
      [sectionName]: isLoading,
    }))
  }, [])

  // Handler to dismiss preview mode by removing the preview query param
  const handleDismissPreview = useCallback(() => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete("preview")
    setSearchParams(newParams)
  }, [searchParams, setSearchParams])

  // Initialise feature toggles (this triggers auto-fetch for the current organization)
  const { resolved: resolvedToggles, loading: togglesLoading } = useFeatureToggles()

  // Track whether feature toggles have been fetched at least once.
  // Sections are created via createRoot and can't be re-rendered with new props,
  // so we must wait for toggles before mounting SectionLoader.
  const togglesReadyRef = useRef(false)
  const togglesFetchedOnceRef = useRef(false)
  const prevOrgKeyRef = useRef<string | null>(null)

  // Reset togglesReady when organization changes so sections don't mount
  // with stale/empty feature toggles during the refetch window.
  const currentOrgKey = activeOrganization?.organization.id || "no-org"
  if (prevOrgKeyRef.current !== null && prevOrgKeyRef.current !== currentOrgKey) {
    togglesReadyRef.current = false
    togglesFetchedOnceRef.current = false
  }
  prevOrgKeyRef.current = currentOrgKey

  // Track when the toggles query has actually completed at least once.
  // A lazy query that hasn't been called yet also reports loading=false,
  // so we need to distinguish "never fetched" from "fetched and got empty results".
  // The signal is: togglesLoading went true->false (query was triggered and completed).
  const togglesWasLoadingRef = useRef(false)
  if (togglesLoading) {
    togglesWasLoadingRef.current = true
  }
  // Also mark as fetched if data arrived (handles cache-and-network returning cached data
  // without ever going through loading=true from the component's perspective)
  if (resolvedToggles && resolvedToggles.length > 0) {
    togglesFetchedOnceRef.current = true
  }
  // Mark as fetched when loading transitions from true to false (query completed)
  if (togglesWasLoadingRef.current && !togglesLoading) {
    togglesFetchedOnceRef.current = true
  }

  if (!togglesLoading && togglesFetchedOnceRef.current) {
    // Ready once the query has completed at least once.
    // This handles orgs with zero toggles (togglesFetchedOnceRef is set by
    // the loading true->false transition, not by data length).
    togglesReadyRef.current = true
  }
  const togglesReady = togglesReadyRef.current

  // `togglesLoading` (an Apollo lazy query) flips false one or more renders
  // BEFORE the query's onCompleted dispatches the resolved toggles into Redux,
  // so there is a settling window where togglesReady is true but resolvedToggles
  // is still []. Mounting a section in that window captures an empty feature
  // snapshot (the section is createRoot-mounted once and never re-mounts on
  // feature changes), so DB-controlled flags like hide_reports_* are silently
  // dropped. Latch `togglesSettled` one commit AFTER loading ends: because
  // `resolvedToggles` is an effect dep, by the time this runs the resolved data
  // has been committed, so an empty result at that point is a genuine empty.
  const [togglesSettled, setTogglesSettled] = useState(false)
  useEffect(() => {
    if (togglesLoading) {
      setTogglesSettled(false)
      return
    }
    if (!togglesFetchedOnceRef.current) return
    // Defer one macrotask so the onCompleted Redux dispatch (and the resulting
    // resolvedToggles update) has flushed before we declare settled.
    const id = setTimeout(() => setTogglesSettled(true), 0)
    return () => clearTimeout(id)
  }, [togglesLoading, resolvedToggles, currentOrgKey])

  // Check if Ask Hydrologiq feature is enabled
  const isAskHydrologiqEnabled = useFeatureEnabled("ask_hydrologiq")

  // Ask Hydrologiq chatbot (only if enabled)
  const { messages, isLoading: isChatLoading, isOpen: isChatOpen, sendMessage, toggleChat, closeChat } = useChatbot()

  // Register Cmd+K / Ctrl+K keyboard shortcut (only if enabled)
  useKeyboardShortcut({
    key: "k",
    callback: toggleChat,
    enabled: isAskHydrologiqEnabled,
  })

  // Query preferences once at top level (now includes features)
  // This will automatically refetch when organization changes via refetchQueries in switchOrganization
  const { data: prefData } = useQuery(gqlTypes.GetPreferencesDocument, {
    fetchPolicy: "cache-and-network",
  })

  const dispatch = useDispatch()

  // Sync promotion overrides from the getPreferences query into Redux.
  // When the active organization changes, switchOrganization triggers a refetch of
  // GetPreferencesDocument (via refetchQueries). This effect picks up the new data
  // and dispatches it so SectionLoader can read overrides from Redux state.
  useEffect(() => {
    const prefs = prefData?.getPreferences
    // promotion_overrides may not exist in the GraphQL response yet (backend change pending).
    // Safely extract it — if undefined, dispatch null to clear any stale overrides.
    const overrides = (prefs as any)?.promotion_overrides ?? null
    dispatch(setPromotionOverrides(overrides))
  }, [prefData, dispatch])

  // Transform to SectionFeatures format
  const sectionFeatures = useMemo<SectionFeatures>(() => {
    const features = prefData?.getPreferences?.features || []
    const result: Record<string, { enabled: boolean; preview: boolean }> = {}

    features.forEach((feature: gqlTypes.FeatureResult) => {
      if (feature?.name) {
        result[feature.name] = {
          enabled: feature.enabled || false,
          preview: feature.preview || false,
        }
      }
    })

    return result
  }, [prefData])

  const sidebarWidth = { base: 55, md: 75 }

  // Merge tenant preferences features with resolved feature toggles.
  // getUserFeatureStates (resolvedToggles) takes priority over the old
  // tenantInformation.preferences.features JSONB blob.
  // We keep the previous merged result during loading to prevent sections
  // from mounting with incomplete features (race between clearOrganizationData
  // and the subsequent getUserFeatureStates refetch).
  const prevMergedFeaturesRef = useRef<Record<string, boolean>>({})
  const mergedTenantFeatures = useMemo(() => {
    const base: Record<string, boolean> = prefData?.getPreferences?.tenantInformation?.preferences?.features ?? {}

    // If toggles are currently loading (cleared but not yet refetched),
    // return the previous merged result to avoid passing incomplete features.
    if (togglesLoading && prevMergedFeaturesRef.current && Object.keys(prevMergedFeaturesRef.current).length > 0) {
      return prevMergedFeaturesRef.current
    }

    if (!resolvedToggles || resolvedToggles.length === 0) {
      prevMergedFeaturesRef.current = base
      return base
    }

    const merged = { ...base }
    for (const toggle of resolvedToggles) {
      merged[toggle.featureKey] = toggle.enabled
    }

    prevMergedFeaturesRef.current = merged
    return merged
  }, [prefData, resolvedToggles, togglesLoading])

  // Guard against mounting a section with a stale features snapshot. The
  // section is created via createRoot and captures `mergedTenantFeatures` once
  // at mount — it never re-mounts on feature changes. There is a render race
  // where `togglesReady` flips true but `mergedTenantFeatures` was memoised in
  // an earlier render (before `resolvedToggles` populated), so the merge hadn't
  // yet folded the toggles in. Only treat features as ready once every resolved
  // toggle key is actually present in the merged object (or there are genuinely
  // no toggles to fold in).
  // Features are ready to mount a section when either the merge has actually
  // folded in the resolved toggles (they're all present in the merged object),
  // or the query has genuinely settled with zero toggles for this org. This
  // closes the settling-window race where resolvedToggles is transiently [].
  const featuresReady =
    (resolvedToggles &&
      resolvedToggles.length > 0 &&
      resolvedToggles.every((t) => t.featureKey in mergedTenantFeatures)) ||
    (togglesSettled && (!resolvedToggles || resolvedToggles.length === 0))

  const topBarHeight = sidebarWidth.md * 0.75
  // Create a key that includes organization ID to force remount when org changes
  const orgKey = currentOrgKey

  const Chat = useMemo(() => {
    return <ChatButton onClick={toggleChat} showShortcut={false} expanded={false} enabled={isAskHydrologiqEnabled} />
  }, [toggleChat, isAskHydrologiqEnabled])

  return (
    <>
      <Flex height="100vh" overflow="hidden" color={"primary"} direction="column" minWidth="375px">
        <Box width="100vw" minWidth="375px" height={`${topBarHeight}px`} padding="5px">
          <Box
            height="100%"
            bg="linear-gradient(100deg, #39aed2 0%, #B8E6E1 200%)"
            borderRadius="5px"
            border={"1px solid #D0D0D0"}
          >
            <TopBar Chat={Chat} />
          </Box>
        </Box>
        <Flex flex={1} height={`calc(100vh - ${topBarHeight}px)`}>
          {/* Sidebar */}
          <Sidebar preferencesData={prefData} Chat={Chat} position="relative" minimal collapsed />

          {/* Main content area */}
          <Box
            flex={1}
            ml={0}
            pr={"5px"}
            minWidth="320px"
            width={{ base: `calc(100vw - ${sidebarWidth.base}px)`, md: `calc(100vw - ${sidebarWidth.md}px)` }}
          // pb={"5px"}
          >
            {/* Section content */}
            <Box overflow="hidden" position="relative" width="100%" minWidth="320px" height="calc(100% - 5px)">
              {/* Loading state - show while section is being determined or loaded */}
              {isLoadingSection && (
                <Skeleton height="100%" width="100%" />
              )}

              {/* Empty state - only show when no sections are active and not on settings */}
              {/* {!currentSection && !isOnGlobalSettings && !isLoadingSection && (
                <Box position="absolute" inset={0} zIndex={1}>
                  <EmptyState />
                </Box>
              )} */}

              {/* Sections - keep mounted but hide when on global settings */}
              <Box display={isOnGlobalSettings ? "none" : "block"} height="100%" position="relative">
                {sectionsToRender.map(({ sectionName, isActive, shouldRender }) => (
                  <Box
                    key={sectionName}
                    visibility={isActive ? "visible" : "hidden"}
                    position={isActive ? "relative" : "absolute"}
                    top={0}
                    left={0}
                    height="100%"
                    width="100%"
                    pointerEvents={isActive ? "auto" : "none"}
                    zIndex={isActive ? 10 : 0}
                  >
                    {shouldRender && togglesReady && featuresReady && (
                      <Suspense
                        fallback={
                          <Center height="100%">
                            <Spinner size="xl" color="blue.500" />
                          </Center>
                        }
                      >
                        <SectionLoader
                          key={`${sectionName}-${orgKey}`}
                          sectionName={sectionName as SectionKey}
                          sidebarWidth={sidebarWidth}
                          sectionFeatures={sectionFeatures}
                          onLoadStateChange={handleSectionLoadStateChange}
                          isActive={isActive}
                          mountedSections={mountedSections}
                          tenantCopy={prefData?.getPreferences?.tenantInformation?.preferences?.copy}
                          tenantFeatures={mergedTenantFeatures}
                          profile={
                            prefData?.getPreferences?.tenantInformation?.preferences?.profile ??
                            TenantProfile.FuelProducer
                          }
                          user={user}
                          previewBundleUrl={previewBundleUrl}
                        />
                      </Suspense>
                    )}
                  </Box>
                ))}
              </Box>

              {/* Settings content - show when on global settings routes */}
              {outletContent && <Box height="100%">{outletContent}</Box>}
            </Box>
          </Box>
        </Flex>
      </Flex>

      {isAskHydrologiqEnabled && (
        <ChatDrawer
          open={isChatOpen}
          onClose={closeChat}
          messages={messages}
          isLoading={isChatLoading}
          onSendMessage={sendMessage}
        />
      )}

      {isPreviewActive && previewHash && (
        <PreviewBanner previewHash={previewHash} onDismiss={handleDismissPreview} />
      )}
    </>
  )
}
